import React, {
	type ReactElement,
	type ReactNode,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';

import { loadEntryPoint } from 'react-relay';

import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
} from '@atlassian/entry-point-types/types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container/ui';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;

type EntryPointProps<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

function setTriggerRef(
	ref: React.Ref<HTMLButtonElement> | undefined,
	element: HTMLButtonElement | null,
): void {
	if (typeof ref === 'function') {
		ref(element);
	} else if (ref) {
		(ref as React.MutableRefObject<HTMLButtonElement | null>).current = element;
	}
}

/**
 * Props passed to a panel trigger's visible control.
 */
export type PanelTriggerRenderProps = {
	isSelected: boolean;
	onClick: () => void;
	ref: React.Ref<HTMLButtonElement>;
};

/**
 * Loads app-supplied entry-point content into a caller-owned page-layout panel.
 *
 * This deliberately owns no panel DOM: callers render the returned content as a
 * direct child of Navigation System `Root`, which preserves Panel's responsive
 * end-panel/overlay behavior. The trigger is supplied by the caller so top-nav
 * items retain their ADS component, semantics and layout placement.
 */
export type PanelTriggerProps<TEntryPoint extends AnyEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointProps<TEntryPoint>, 'closePanel'>;
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	isPanelOpen: boolean;
	onClosePanel: () => void;
	onError?: ErrorCallback;
	onOpenPanel: (content: ReactNode) => void;
	trigger: (props: PanelTriggerRenderProps) => ReactElement;
	triggerRef?: React.Ref<HTMLButtonElement>;
};

export function PanelTrigger<TEntryPoint extends AnyEntryPoint>({
	entryPoint,
	entryPointParams,
	entryPointProps,
	errorFallback,
	fallback,
	isPanelOpen,
	onClosePanel,
	onError,
	onOpenPanel,
	trigger,
	triggerRef,
}: PanelTriggerProps<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo(
		() => (entryPoint.root as JSResourceReference<unknown>).getModuleName() || 'unknown',
		[entryPoint],
	);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const createContent = useCallback(
		(entryPointReference: EntryPointRef<TEntryPoint>) => (
			<InternalEntryPointContainer
				id={id}
				entryPointReference={entryPointReference}
				fallback={fallback}
				errorFallback={errorFallback}
				runtimeProps={
					{
						...(entryPointProps ?? {}),
						closePanel: onClosePanel,
					} as unknown as EntryPointProps<TEntryPoint>
				}
				onError={onError}
			/>
		),
		[entryPointProps, errorFallback, fallback, id, onClosePanel, onError],
	);

	// The native click listener is only rebound when the trigger's ref callback
	// changes identity. Read the committed open state through a ref so a listener
	// that was not rebound (for example, a consumer holding a stable ref) still
	// toggles correctly instead of re-opening an already open panel.
	const isPanelOpenRef = useRef(isPanelOpen);
	useLayoutEffect(() => {
		isPanelOpenRef.current = isPanelOpen;
	}, [isPanelOpen]);

	const onLoad = useCallback(
		({ reference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// This is the single owner of the open/close toggle. `usePressablePreloadRef`
			// invokes it from a native click listener on the trigger, which runs before
			// React's delegated `onClick`, so the ref still holds the pre-click value.
			// Hover and pointer-down only preload; they never reach this callback.
			if (isPanelOpenRef.current) {
				onClosePanel();
				return;
			}

			onOpenPanel(createContent(reference));
		},
		[createContent, onClosePanel, onOpenPanel],
	);

	const preloadTriggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({ load, onLoad });
	const mergedTriggerRef = useCallback(
		(element: HTMLButtonElement | null) => {
			setTriggerRef(preloadTriggerRef, element);
			setTriggerRef(triggerRef, element);
		},
		[preloadTriggerRef, triggerRef],
	);

	// Intentionally a no-op: opening and closing are handled in `onLoad` above.
	// In a real browser, React flushes the open-panel update between the native
	// listener and this delegated handler, so toggling here as well would see the
	// just-committed open state and immediately close the panel again.
	const handleClick = useCallback(() => {}, []);

	return trigger({
		isSelected: isPanelOpen,
		onClick: handleClick,
		ref: mergedTriggerRef,
	});
}
